import { createSelector } from "reselect";
import { RootState } from "../reducers";
import {
  mapCaseToDistributionLists,
  mapCaseAndUsersToDistributionList,
  relateUserToCaseFollow,
} from "./utils";

export const selectDistributionLists = (state: RootState) =>
  state.distributionLists.distributionLists.data;

export const selectDistributionListsLoading = (state: RootState) =>
  state.distributionLists.isLoading;

export const selectDistributionListsNavLoading = (state: RootState) =>
  state.distributionLists.navLoading;

export const selectDistributionListsPage = (state: RootState) =>
  state.distributionLists.page;

export const selectDistributionListsFilteredTotal = (state: RootState) =>
  state.distributionLists.filteredTotal;

export const selectDistributionListsFilteredCount = createSelector(
  selectDistributionLists,
  distributionLists => distributionLists.length,
);

export const selectDistributionListsMeta = (state: RootState) =>
  state.distributionLists.distributionLists.meta;

export const selectDistributionListsRecordCount = createSelector(
  selectDistributionListsMeta,
  meta => meta && meta.recordCount,
);

export const selectDistributionListsPageCount = createSelector(
  selectDistributionListsMeta,
  meta => meta && meta.pageCount,
);

export const selectDistributionListsIncluded = (state: RootState) =>
  state.distributionLists.distributionLists.included;

export const selectDistributionListsLinks = (state: RootState) =>
  state.distributionLists.distributionLists.links;

export const selectSelectedDistributionList = (state: RootState) =>
  state.distributionLists.distributionList.data;

export const selectSelectedDistributionListLoading = (state: RootState) =>
  state.distributionLists.selectedLoading;

export const selectSelectedDistributionListIncluded = (state: RootState) =>
  state.distributionLists.distributionList.included;

export const selectSelectedDistributionListRelatedCase = (state: RootState) =>
  state.distributionLists.distributionList.data.relationships.case;

export const selectSelectedDistributionListRecentlyAdded = (state: RootState) =>
  state.distributionLists.distributionList.recentlyAdded;

export const selectSelectedDistributionListRecentlyAddedCaseTitle = (
  state: RootState,
) => state.distributionLists.distributionList.recentlyAddedCaseTitle;

export const selectSelectedRelatedUsers = createSelector(
  selectSelectedDistributionListIncluded,
  included => included.filter(related => related.type === "users"),
);

export const selectSelectedRelatedCaseFollows = createSelector(
  selectSelectedDistributionListIncluded,
  included => included.filter(related => related.type === "caseFollows"),
);

export const selectIncludedUsers = createSelector(
  selectDistributionListsIncluded,
  included => included.filter(related => related.type === "users"),
);

export const selectIncludedCases = createSelector(
  selectDistributionListsIncluded,
  included => included.filter(related => related.type === "cases"),
);

export const selectIncludedCaseFollows = createSelector(
  selectDistributionListsIncluded,
  included => included.filter(related => related.type === "caseFollows"),
);

export const selectSelectedRelatedCase = createSelector(
  selectSelectedDistributionListRelatedCase,
  selectSelectedDistributionListIncluded,
  (relatedCase, included) =>
    included.find(
      related =>
        relatedCase && relatedCase.data && related.id === relatedCase.data.id,
    ) || {},
);

export const selectSelectedMappedFollows = createSelector(
  selectSelectedRelatedCaseFollows,
  selectSelectedRelatedUsers,
  (caseFollows, users) =>
    caseFollows
      .map(caseFollow => {
        const relatedUser = relateUserToCaseFollow(caseFollow, users);
        return {
          caseFollowId: caseFollow.id,
          userId: relatedUser && relatedUser.id,
          name: relatedUser
            ? `${relatedUser.attributes.firstName || ""} ${relatedUser
                .attributes.lastName || ""}`
            : caseFollow.attributes.email,
          ...caseFollow.attributes,
          ...(relatedUser && relatedUser.attributes),
        };
      })
      /**
       * some users have no id value (staging data is corrupt)
       * filtering out falsy values
       */
      .filter(caseFollow => caseFollow),
);

export const selectSortedCaseFollowsWithUser = (
  sortBy?: string,
  direction?: string,
) =>
  createSelector(
    () => sortBy,
    () => direction,
    selectSelectedMappedFollows,
    (sortBy, direction, follows) => {
      if (sortBy && direction) {
        return follows.sort((a, b) => {
          if (direction === "ASC") {
            if (a[sortBy] < b[sortBy]) {
              return -1;
            }
            if (a[sortBy] > b[sortBy]) {
              return 1;
            }
            return 0;
          }
          if (a[sortBy] < b[sortBy]) {
            return 1;
          }
          if (a[sortBy] > b[sortBy]) {
            return -1;
          }
          return 0;
        });
      }
      return follows;
    },
  );

export const selectSelectedDistributionListWithCase = createSelector(
  selectSelectedDistributionList,
  selectSelectedRelatedCase,
  (distributionList, userCase) => ({
    distributionListId: distributionList.id,
    caseId: userCase.id,
    ...distributionList.attributes,
    ...userCase.attributes,
  }),
);

export const selectMappedDistributionLists = createSelector(
  selectDistributionLists,
  selectDistributionListsIncluded,
  mapCaseToDistributionLists,
);

export const selectMappedSingleDistributionList = createSelector(
  selectSelectedDistributionList,
  selectSelectedDistributionListIncluded,
  mapCaseAndUsersToDistributionList,
);

export const selectLitigationDrawerDistributionList = (
  sort?: string,
  direction?: string,
) =>
  createSelector(
    selectSelectedDistributionList,
    selectSelectedRelatedCase,
    selectSortedCaseFollowsWithUser(sort, direction),
    selectSelectedDistributionListRecentlyAdded,
    (distributionList, userCase, follows, newFollows) => {
      const sortedFollows = follows.sort((a, b) => {
        if (
          newFollows.includes(a.caseFollowId) &&
          !newFollows.includes(b.caseFollowId)
        ) {
          return -1;
        }
        return 1;
      });
      if (distributionList && userCase && follows) {
        return {
          distributionListId: distributionList.id,
          caseId: userCase.id,
          follows: sortedFollows,
          ...distributionList.attributes,
          ...userCase.attributes,
        };
      }
    },
  );
